import { useEffect, useState } from "react"
import { getParticipants } from "@/services/participants"
import { cachedRead } from "@/utils/readCache"
import type { Participant } from "@/types"

/*
 * The roster for one session, for an avatar stack.
 *
 * A one-shot read per session rather than a listener: nothing on a list screen
 * needs the roster to move as people join. Callers pay one query per session,
 * so only use it on lists that are bounded — the customer's sessions, or the
 * facilitator's own.
 *
 * Per SESSION, not per mount, which is the difference `cachedRead` makes and
 * why it is here: a row is unmounted and remounted every time the workspace's
 * state filter moves or its view is toggled, and each of those was its own
 * query for a roster the screen had already read a second earlier.
 */
export function useSessionParticipants(sessionId: string): Participant[] {
  const [participants, setParticipants] = useState<Participant[]>([])
  useEffect(() => {
    let live = true
    cachedRead(`participants:${sessionId}`, () => getParticipants(sessionId)).then((loaded) => {
      if (live) setParticipants(loaded)
    }, noop)
    return () => {
      live = false
    }
  }, [sessionId])
  return participants
}

function noop() {}
